import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import CouponCard from "@/components/CouponCard";
import { ExternalLink, Star, BadgeCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PageBackground from "@/components/PageBackground";
import Seo from "@/components/Seo";
import TrustBadge from "@/components/TrustBadge";

export default function StoreDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const { user, refresh } = useAuth();

  useEffect(() => {
    setMissing(false);
    api.get(`/stores/${slug}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing) return (
    <div className="max-w-4xl mx-auto px-6 py-16 text-center text-sm text-slate-500" data-testid="store-detail-404">
      Store not found. <Link to="/stores" className="text-cyan-700 underline">Browse all stores</Link>
    </div>
  );
  if (!data) return (
    <div className="relative"><PageBackground variant="mint-cyan" /><div className="max-w-7xl mx-auto px-6 py-12 relative z-10 text-slate-500">Loading...</div></div>
  );
  const { store, coupons } = data;
  const isFav = user && user.favorite_stores && user.favorite_stores.includes(store.id);

  const toggleFav = async () => {
    if (!user) return toast("Sign in to favorite stores");
    if (isFav) await api.delete(`/user/favorite-store/${store.id}`);
    else await api.post(`/user/favorite-store/${store.id}`);
    refresh();
  };

  return (
    <div className="relative">
      <Seo
        title={`${store.name} coupons & promo codes`}
        description={`Save on ${store.name}${store.verified ? " — a verified brand on Cyannect" : ""}. Browse ${coupons.length} live coupons and deals for Canada & US.`}
        image={store.logo_url}
        path={`/store/${store.slug}`}
        type="article"
      />
      <PageBackground variant="mint-cyan" />
      <div className="max-w-7xl mx-auto px-6 py-12 relative z-10">
        <div className="glass-strong rounded-3xl p-8 flex flex-col md:flex-row items-start md:items-center gap-6 mb-10 fade-up">
          <img src={store.logo_url} alt={store.name} className="w-24 h-24 rounded-2xl border border-white/80 bg-white object-contain shadow" data-logo="1" />
          <div className="flex-1">
            <div className="pf-chip pf-chip-cyan">Store</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-[-0.02em] mt-3 text-slate-900 flex items-center gap-3 flex-wrap">
              {store.name}
              {store.verified && (
                <span className="inline-flex items-center gap-1.5 text-cyan-700 text-sm font-medium bg-cyan-50 border border-cyan-200 rounded-full px-3 py-1" title="Verified brand" data-testid="store-verified-badge">
                  <BadgeCheck className="w-4 h-4" /> Verified brand
                </span>
              )}
              <TrustBadge trust={store.trust} size="lg" testid="store-trust-badge" />
            </h1>
            <p className="text-slate-600 mt-3 max-w-2xl">{store.description}</p>
          </div>
          <div className="flex gap-3">
            {store.website && (
              <a href={store.website} target="_blank" rel="noreferrer" className="pf-btn-glass px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2" data-testid="store-visit">
                Visit site <ExternalLink className="w-4 h-4" />
              </a>
            )}
            <button onClick={toggleFav} className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 ${isFav ? "pf-btn-primary" : "pf-btn-glass"}`} data-testid="store-favorite">
              <Star className="w-4 h-4" /> {isFav ? "Favorited" : "Favorite"}
            </button>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {coupons.map((c) => <CouponCard key={c.id} coupon={{ ...c, store_name: store.name, store_slug: store.slug, store_logo: store.logo_url }} />)}
        </div>
      </div>
    </div>
  );
}
